import { useStoreAuth } from "../store";
import useAPI from "./useAPI";

export default function useCompany() {
  const api = useAPI();
  const { client } = useStoreAuth();
  const { data: companies, isPending: isCompaniesPending, refetch: refetchCompanies } = client.useListOrganizations()

  if (typeof window !== 'undefined') {
    const pathname = window.location?.pathname;
  }
  const { data: activeCompany, isPending: isActiveCompanyPending, refetch: refetchActiveCompany} = client.useActiveOrganization()

  const createCompany = async ({name}) => {
    await client.organization.create({
      name: name, // required
      slug: name.toLowerCase().replace(/\s+/g, '-'),
      keepCurrentActiveOrganization: false,
    });
  }

  const selectCompany = async (companyId) => {
    await client.organization.setActive({
      organizationId: companyId
    });
  }

  const getAllCompanies = async () => {
    try {
      return await api.getAdminCompanies();
    } catch (error) {
      console.error("Error fetching all companies:", error);
      return [];
    }
  };

  const deleteCompanies = async (companyId) => {
    try {
      return await api.deleteAdminCompanies(companyId);
    } catch (error) {
      console.error("Error deleting company:", error);
      return { error };
    }
  };
  
  const getCompanyMembers = async (page = 1) => {
    const { data, error } = await client.organization.listMembers({
        query: {
            organizationId: activeCompany.id,
            limit: 100,
            offset: ((page || 1) - 1) * 100,
        },
    });
    if (error) {
      console.error("Error fetching organization members:", error);
      return [];
    }
    return data || [];
  }

  const getCompanyUsers = async (companyId) => {
    try {
      const result = await api.getCompanyUsers(companyId);
      return result;
    } catch (error) {
      console.error("[useCompany] Error fetching company users:", error);
      return [];
    }
  }

  const inviteMember = async (email) => {
    const { data, error } = await client.organization.inviteMember({
      email: email,
      role: "admin",
      organizationId: activeCompany.id,
      resend: true,
    });
    if (error) {
      console.error("Error inviting member:", error);
      return { error };
    }
    return { data };
  }

  const cancelInvitation = async (invitationId) => {
    const { data, error } = await client.organization.cancelInvitation({
      invitationId: invitationId,
    });
    if (error) {
      console.error("Error cancelling invitation:", error);
      return { error };
    }
    return { data };
  }

  const getInvitations = async () => {
    const { data, error } = await client.organization.listInvitations({
      query: {
        organizationId: activeCompany.id,
      },
    });
    if (error) {
      console.error("Error fetching invitations:", error);
      return [];
    }
    return data || [];
  }

  const updateMemberRole = async (memberId, role) => {
    const { data, error } = await client.organization.updateMemberRole({
      memberId,
      role,
      organizationId: activeCompany.id,
    });
    if (error) {
      console.error("Error updating member role:", error);
      throw error;
    }
    return data;
  };

  const removeMember = async (memberId) => {
    const { data, error } = await client.organization.removeMember({
      memberIdOrEmail: memberId,
      organizationId: activeCompany.id,
    });
    if (error) {
      console.error("Error removing member:", error);
      throw error;
    }
    return data;
  };

  const getCompanyDetails = async (companyId) => {
    try {
      return await api.getCompanyDetails(companyId);
    } catch (error) {
      console.error("Error fetching company details:", error);
      return null;
    }
  }

  const updateCompany = async (companyId, updateData = {}) => {
    const { data, error } = await client.organization.update({
      organizationId: companyId,
      data: updateData,
    });
    if (error) {
      throw error;
    }
    return { data };
  }

  const addMember = async (companyId) => {
    try {
      return await api.addMemberToCompany(companyId);
    } catch (error) {
      console.error("Error adding member to company:", error);
      return { error };
    }
  }

  const deleteCompany = async (companyId) => {
    try {
      return await api.deleteAdminCompanies(companyId);
    } catch (error) {
      console.error("Error deleting company:", error);
      return { error };
    }
  }

  const companyOverview = async (companyId) => {
    try {
      return await api.getCompanyOverview(companyId);
    } catch (error) {
      console.error("Error fetching company overview:", error);
      return null;
    }
  }

  return {
    createCompany,
    selectCompany,
    companies, activeCompany,
    isActiveCompanyPending, isCompaniesPending,
    getCompanyMembers,
    inviteMember, getInvitations, cancelInvitation,
    refetchCompanies, refetchActiveCompany,
    getAllCompanies,
    deleteCompanies,
    updateCompany,
    updateMemberRole,
    removeMember,
    getCompanyDetails,
    addMember,
    deleteCompany,
    companyOverview,
    getCompanyUsers
  };
}
